import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import type { GasOrderSummary } from '@tucupet/shared';
import { PageHeader, Card, Btn, Table } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { GasStatusBadge } from './gas-shared';

export default function GasDetailPage() {
  const { code = '' } = useParams();
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'gas', 'detail', code] });
    qc.invalidateQueries({ queryKey: ['admin', 'gas'] });
  };

  const { data, isLoading } = useQuery<GasOrderSummary>({
    queryKey: ['admin', 'gas', 'detail', code],
    queryFn: () => api(`/api/admin/gas/orders/${code}`),
  });

  const fulfill = useMutation({
    mutationFn: () => api(`/api/admin/gas/orders/${code}/fulfill`, { body: {} }),
    onSuccess: invalidate,
  });
  const cancel = useMutation({
    mutationFn: () => api(`/api/admin/gas/orders/${code}/cancel`, { body: {} }),
    onSuccess: invalidate,
  });

  if (isLoading || !data) {
    return (
      <div>
        <Link to="/gas" className="mono" style={{ color: 'var(--text-3)', fontSize: 12, textDecoration: 'none' }}>← Pedidos de gas</Link>
        <p style={{ color: 'var(--text-3)', marginTop: 24 }}>Cargando…</p>
      </div>
    );
  }

  const err = fulfill.isError ? fulfill.error : cancel.isError ? cancel.error : null;
  const canFulfill = data.status === 'paid';
  const canCancel = !['fulfilled', 'cancelled', 'expired'].includes(data.status);

  return (
    <div>
      <Link to="/gas" className="mono" style={{ color: 'var(--text-3)', fontSize: 12, textDecoration: 'none' }}>← Pedidos de gas</Link>
      <div style={{ height: 12 }} />
      <PageHeader
        kicker="PEDIDO DE GAS"
        title={data.code}
        sub={`${data.deliveryMode === 'pickup' ? 'Recogida' : 'Entrega a domicilio'} · ${new Date(data.createdAt).toLocaleString('es-MX')}`}
        action={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <GasStatusBadge status={data.status} />
            {canFulfill && <Btn size="sm" onClick={() => fulfill.mutate()} disabled={fulfill.isPending}>{fulfill.isPending ? '…' : 'Marcar entregada'}</Btn>}
            {canCancel && <Btn variant="danger" size="sm" onClick={() => cancel.mutate()} disabled={cancel.isPending}>{cancel.isPending ? '…' : 'Cancelar'}</Btn>}
          </div>
        }
      />

      {err && <div style={{ marginBottom: 16, color: 'var(--danger)', fontSize: 12 }}>{(err as ApiError)?.message ?? 'Error'}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 16, alignItems: 'start' }}>
        <Card padding={0}>
          <Table<GasOrderSummary['items'][number]>
            rows={data.items}
            columns={[
              { key: 'name', label: 'Artículo', render: (l) => <span>{l.name} <span className="mono" style={{ fontSize: 10, color: 'var(--text-3)' }}>{l.kind === 'cylinder' ? 'BALITA' : 'ACCESORIO'}</span></span> },
              { key: 'qty', label: 'Cant.', render: (l) => <span className="mono">{l.quantity}</span> },
              { key: 'unit', label: 'Precio', render: (l) => <span className="mono">${l.unitPrice.toFixed(2)}</span> },
              { key: 'total', label: 'Subtotal', render: (l) => <span className="mono" style={{ fontWeight: 600 }}>${l.lineTotal.toFixed(2)}</span> },
            ]}
          />
          <div style={{ padding: 16, borderTop: '1px solid var(--border)', display: 'grid', gap: 6 }}>
            <Row label="Subtotal" value={`$${data.subtotal.toFixed(2)}`} />
            <Row label="Envío" value={data.deliveryFee > 0 ? `$${data.deliveryFee.toFixed(2)}` : 'Gratis / recogida'} />
            <Row label="Total" value={`$${data.total.toFixed(2)}`} strong />
          </div>
        </Card>

        <div style={{ display: 'grid', gap: 16 }}>
          <Card>
            <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.1em', marginBottom: 12 }}>CLIENTE</div>
            <Row label="Nombre" value={data.customer?.name ?? '—'} />
            <Row label="Email" value={data.customer?.email ?? '—'} />
            <Row label="Teléfono" value={data.deliveryPhone ?? '—'} />
          </Card>
          <Card>
            <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.1em', marginBottom: 12 }}>ENTREGA</div>
            {data.deliveryMode === 'pickup' ? (
              <Row label="Recogida en" value={data.pickupPointName ?? '—'} />
            ) : (
              <>
                <Row label="Dirección" value={data.deliveryAddress ?? '—'} />
                <Row label="Municipio" value={[data.deliveryMunicipality, data.deliveryProvince].filter(Boolean).join(', ') || '—'} />
              </>
            )}
            <Row label="Balitas vacías a entregar" value={String(data.emptyCylindersCommitted)} />
            <Row label="Pago" value={data.paymentMethod === 'wallet' ? 'Saldo' : data.paymentMethod === 'card' ? 'Tarjeta/transferencia' : '—'} />
            <Row label="Pagado" value={data.paidAt ? new Date(data.paidAt).toLocaleString('es-MX') : '—'} />
            <Row label="Entregado" value={data.fulfilledAt ? new Date(data.fulfilledAt).toLocaleString('es-MX') : '—'} />
            {data.notes && <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-2)' }}>📝 {data.notes}</div>}
          </Card>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '5px 0' }}>
      <span className="mono" style={{ fontSize: 11, color: 'var(--text-3)' }}>{label}</span>
      <span style={{ fontSize: 13, textAlign: 'right', fontWeight: strong ? 700 : 400 }}>{value}</span>
    </div>
  );
}
